import React from "react";
import { Textarea } from "@shadcn-in-nx/ui/textarea";
import { Button } from "@shadcn-in-nx/ui/button";
import { Copy, Expand } from "lucide-react";
import { toast } from "sonner";
import {
    ContextMenu,
    ContextMenuContent,
    ContextMenuItem,
    ContextMenuTrigger,
} from "@shadcn-in-nx/ui/context-menu";
import FieldInput from "./FieldInput";
import { inferFieldComponent } from "../utils/inferInputFieldComponent";
import { Checkbox } from "@shadcn-in-nx/ui/checkbox";

const ROW_HEIGHT = 36;
const EXPANDED_HEIGHT = 72;

const TableCell = React.forwardRef(
    (
        {
            record,
            column,
            isEditing,
            isNew,
            value,
            onCellClick,
            onSave,
            savingRecordId,
            onClearEditingCell,
            onOpenModal,
        },
        ref
    ) => {
        const inputRef = React.useRef(null);
        const [localValue, setLocalValue] = React.useState(() => {
            if (typeof value === "object" && value !== null) {
                return JSON.stringify(value, null, 2);
            }
            return value ?? "";
        });

        React.useEffect(() => {
            if (typeof value === "object" && value !== null) {
                setLocalValue(JSON.stringify(value, null, 2));
            } else {
                setLocalValue(value ?? "");
            }
        }, [value]);

        React.useEffect(() => {
            if (isEditing && inputRef.current) {
                inputRef.current.focus();
                inputRef.current.select();
            }
        }, [isEditing]);

        const handleBlur = () => {
            if (savingRecordId === record.id) return;
            let valueToSave = localValue;
            if (typeof localValue === "string" && (localValue.trim().startsWith("{") || localValue.trim().startsWith("["))) {
                try { valueToSave = JSON.parse(localValue); } catch {}
            }
            onSave(record.id, valueToSave, column.name);
            onClearEditingCell();
        };

        const handleKeyDown = (e) => {
            if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleBlur();
            }
            if (e.key === "Escape") {
                e.preventDefault();
                setLocalValue(typeof value === "object" ? JSON.stringify(value, null, 2) : (value ?? ""));
                onClearEditingCell();
            }
        };

        const copyValue = async () => {
            const text = typeof value === "object" ? JSON.stringify(value, null, 2) : String(value ?? "");
            await navigator.clipboard.writeText(text);
            toast.success("Copied");
        };

        const handleDoubleClick = () => {
            if (componentType === "boolean") return;
            onCellClick(record.id, column.name);
        };

        const handleOpenInModal = () => {
            onOpenModal?.(record.id, column.name, value);
        };

        const componentType = inferFieldComponent(column);

        const displayValue = () => {
            if (value === null || value === undefined || value === "") {
                return <span className="text-gray-400 italic">null</span>;
            }

            if (componentType === "boolean") {
                return (
                    <div className="flex items-center h-full" onClick={(e) => e.stopPropagation()}>
                        <Checkbox 
                            checked={Boolean(value)} 
                            onCheckedChange={(checked) => {
                                onSave(record.id, checked, column.name);
                            }}
                        />
                    </div>
                );
            }

            if (componentType === "url") {
                return (
                    <a 
                        href={value} 
                        target="_blank" 
                        rel="noreferrer" 
                        className="text-blue-500 hover:underline truncate"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {value}
                    </a>
                );
            }

            if (componentType === "datetime" || componentType === "time") {
                try {
                    const d = new Date(value);
                    if (!isNaN(d.getTime())) {
                        return d.toLocaleString();
                    }
                } catch (e) {}
            }

            if (componentType === "date") {
                try {
                    const d = new Date(value);
                    if (!isNaN(d.getTime())) {
                        return d.toLocaleDateString();
                    }
                } catch (e) {}
            }

            if (typeof value === "object") {
                return JSON.stringify(value);
            }
            return String(value);
        };

        // Boolean component does not use the FieldInput edit mode overlay.
        // It's always inline interactive.
        const isEditMode = (isEditing || isNew) && componentType !== "boolean";
        // Only multi-line / large inputs need the expanded row height.
        const needsExpandedHeight = componentType === "textarea" || componentType === "json-dialog";
        const cellHeight = isEditMode && needsExpandedHeight ? EXPANDED_HEIGHT : ROW_HEIGHT;

        return (
            <ContextMenu>
                <ContextMenuTrigger asChild>
                    <div
                        ref={ref}
                        className="w-full border-r border-border"
                        style={{ height: cellHeight }}
                        onDoubleClick={handleDoubleClick}
                    >
                        {isEditMode ? (
                            <FieldInput
                                column={column}
                                value={value}
                                onSave={(newValue) => onSave(record.id, newValue, column.name)}
                                onCancel={onClearEditingCell}
                                className="w-full h-full border-0 rounded-none bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-inset text-sm font-mono resize-none"
                            />
                        ) : (
                            <div
                                className="h-full px-2 flex items-center overflow-hidden cursor-default hover:bg-muted/50 relative group/cell"
                            >
                                <span className="truncate text-sm pr-12 w-full flex items-center h-full">
                                    {componentType === "boolean" && (value === null || value === undefined || value === "") 
                                        ? (
                                            <div className="flex items-center h-full" onClick={(e) => e.stopPropagation()}>
                                                <Checkbox 
                                                    checked={false} 
                                                    onCheckedChange={(checked) => {
                                                        onSave(record.id, checked, column.name);
                                                    }}
                                                />
                                            </div>
                                          )
                                        : displayValue()
                                    }
                                </span>
                                <div className="hidden group-hover/cell:flex items-center gap-0.5 absolute right-1 top-1/2 -translate-y-1/2 bg-white/90">
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={(e) => { e.stopPropagation(); copyValue(); }}
                                        className="h-6 w-6 p-0"
                                    >
                                        <Copy className="w-3 h-3" />
                                    </Button>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={(e) => { e.stopPropagation(); handleOpenInModal(); }}
                                        className="h-6 w-6 p-0"
                                    >
                                        <Expand className="w-3 h-3" />
                                    </Button>
                                </div>
                            </div>
                        )}
                    </div>
                </ContextMenuTrigger>
                <ContextMenuContent>
                    <ContextMenuItem onClick={handleDoubleClick} disabled={column.system}>
                        Edit cell
                    </ContextMenuItem>
                    <ContextMenuItem onClick={handleOpenInModal}>
                        View/Edit in modal
                    </ContextMenuItem>
                    <ContextMenuItem onClick={copyValue}>
                        Copy value
                    </ContextMenuItem>
                    <ContextMenuItem
                        onClick={() => onSave(record.id, null, column.name)}
                        disabled={column.name === "id" || column.name === "key" || column.system}
                    >
                        Set null
                    </ContextMenuItem>
                </ContextMenuContent>
            </ContextMenu>
        );
    }
);

TableCell.displayName = "TableCell";
export default React.memo(TableCell);
